import OrderApi from '@apis/order/OrderApi';
import couponQueries from '@apis/queries/couponQueries';
import orderLogQueries from '@apis/queries/orderLogQueries';
import orderQueries from '@apis/queries/orderQueries';
import pointQueries from '@apis/queries/pointQueries';
import stampQueries from '@apis/queries/stampQueries';
import userQueries from '@apis/queries/userQueries';
import useCurrency from '@hooks/item/useCurrency';
import useToast from '@hooks/ui/useToast';
import { ErrorResponse } from '@models/error';
import { useFlow } from '@stackflow/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderRefundToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { setIsPrepaidCardBalanceUpdated } from '@utils/prepaidCardUtils';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

function useCancelOrder(orderNo: string) {
    const { t } = useTranslation('orderLog');
    const { pop } = useFlow();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: order } = useQuery(orderLogQueries.detail(orderNo));
    const currency = useCurrency();
    const { mutate } = useMutation({
        mutationFn: OrderApi.cancelOrder,
        onSuccess() {
            pushEcommerceEvent('refund', orderRefundToGtmFormat({ orderNo, order, currency }));
            pop();
            toast.info(t('toast.cancelSuccess'));
            queryClient.resetQueries({ queryKey: orderQueries._def });
            queryClient.resetQueries({ queryKey: orderLogQueries._def });
            queryClient.resetQueries({ queryKey: userQueries._def });
            queryClient.resetQueries({ queryKey: stampQueries._def });
            queryClient.resetQueries({ queryKey: couponQueries._def });
            queryClient.resetQueries({ queryKey: pointQueries._def });
            if (order && order?.prepaidCardAmount !== 0) {
                setIsPrepaidCardBalanceUpdated(true);
            }
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('toast.cancelError'));
        },
    });
    return () => mutate(orderNo);
}

export default useCancelOrder;
